/**
 * PoiGroupsTableRow - Linha da tabela de grupos de POIs
 * Memoizado para evitar re-renders desnecessários
 */

import React, { memo } from 'react';
import {
  TableRow,
  TableCell,
  Typography,
  Avatar,
  Checkbox,
  Box,
} from '@mui/material';
import CategoryIcon from '@mui/icons-material/Category';

import { usePremiumTokens } from '../../common/theme/usePremiumTokens';
import CollectionActions from './CollectionActions';

const PoiGroupsTableRow = memo(({
  row,
  onRefresh,
  selected = false,
  onSelect,
}) => {
  const tokens = usePremiumTokens();

  return (
    <TableRow
      hover
      selected={selected}
      sx={{
        cursor: 'default',
        transition: tokens?.transitions?.colors || 'all 0.15s ease',
        '&:hover': {
          backgroundColor: tokens?.surface?.hover || 'rgba(255,255,255,0.03)',
        },
        '&.Mui-selected': {
          backgroundColor: `${tokens?.accent?.primary}10`,
          '&:hover': {
            backgroundColor: `${tokens?.accent?.primary}15`,
          },
        },
      }}
    >
      {/* Checkbox */}
      {onSelect && (
        <TableCell padding="checkbox" sx={{ py: 1, width: 48 }}>
          <Checkbox
            checked={selected}
            onChange={() => onSelect(row.id)}
            sx={{
              color: tokens?.text?.tertiary,
              '&.Mui-checked': {
                color: tokens?.accent?.primary,
              },
            }}
          />
        </TableCell>
      )}

      {/* Avatar */}
      <TableCell sx={{ py: 1, width: 48 }}>
        <Avatar
          sx={{
            width: 32,
            height: 32,
            bgcolor: row.color ? `${row.color}20` : `${tokens?.semantic?.info}20`,
            color: row.color || tokens?.semantic?.info,
          }}
        >
          <CategoryIcon fontSize="small" />
        </Avatar>
      </TableCell>

      {/* ID */}
      <TableCell sx={{ py: 1, width: 60 }}>
        <Typography variant="body2" sx={{ color: tokens?.text?.muted, fontFamily: 'monospace', fontSize: '0.8rem' }}>
          {row.id}
        </Typography>
      </TableCell>

      {/* Name */}
      <TableCell sx={{ py: 1 }}>
        <Typography variant="body2" fontWeight={500}>
          {row.name}
        </Typography>
      </TableCell>

      {/* Color */}
      <TableCell sx={{ py: 1, width: 80 }}>
        {row.color ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              sx={{
                width: 20,
                height: 20,
                borderRadius: '50%',
                backgroundColor: row.color,
                border: `2px solid ${tokens?.border?.subtle || 'rgba(255,255,255,0.1)'}`,
                flexShrink: 0,
              }}
            />
            <Typography
              variant="caption"
              sx={{ color: tokens?.text?.muted, fontFamily: 'monospace', fontSize: '0.7rem' }}
            >
              {row.color}
            </Typography>
          </Box>
        ) : (
          <Typography variant="body2" sx={{ color: tokens?.text?.muted }}>-</Typography>
        )}
      </TableCell>

      {/* Description */}
      <TableCell sx={{ py: 1 }}>
        <Typography variant="body2" sx={{ color: tokens?.text?.secondary, fontSize: '0.8rem' }}>
          {row.description || '-'}
        </Typography>
      </TableCell>

      {/* Actions */}
      <TableCell align="right" sx={{ py: 1 }}>
        <CollectionActions
          itemId={row.id}
          editPath="/settings/poigroup"
          endpoint="poigroups"
          setTimestamp={onRefresh}
        />
      </TableCell>
    </TableRow>
  );
});

PoiGroupsTableRow.displayName = 'PoiGroupsTableRow';

export default PoiGroupsTableRow;
